import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api, HttpError } from "@/lib/api";
import { QUEST_META, ICON_MAP } from "@/lib/quests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Sparkles, Lock, Check, Star } from "lucide-react";

const AMAZON_REVIEWS = [
  {
    id: 1,
    title: "Clear and Precise — A must read for all founders",
    quote:
      "I have been a part of Praveen's founder community for over 5 years now. The 1000 Founders Community is one of the best out there for founders, and I had been waiting for the book to publish to get it. The book is excellent. It's not theoretical. The approach is very practical and I would recommend this to all founders.",
    name: "Ekdali",
    format: "Hardcover",
    date: "21 July 2025",
    verified: true,
  },
  {
    id: 2,
    title: "A Practical Blueprint for Dreamers Who Dare to Build",
    quote:
      "This book is a true playbook for anyone who wants to escape the 9–5 and build something meaningful. Praveen has beautifully explained frameworks like the Problem-Solution Fit Canvas and how to scale from zero to $10M with practical steps. It's not just theory — it feels like a mentor guiding you step by step.",
    name: "Harsh Vardhan Singh",
    format: "Kindle Edition",
    date: "25 August 2025",
    verified: false,
  },
  {
    id: 3,
    title: "A Must Read! Wonderfully crafted book",
    quote:
      "This book is amazing! The Founder Mode is a practical and empowering guide for aspiring entrepreneurs. It walks you through escaping the 9–5 grind, validating ideas, and scaling smartly. A must-read for anyone serious about building a business from scratch.",
    name: "Sandhya Lakshminarayana",
    format: "Kindle Edition",
    date: "7 August 2025",
    verified: false,
  },
  {
    id: 4,
    title: "Escape 9 to 5",
    quote:
      "A solid mix of practical advice and strategic insight, perfect for aspiring entrepreneurs or even established owners looking to fine-tune their approach. The clear structure, actionable frameworks, and real-world case studies make it not just a theoretical read, but a toolkit that can be applied in real startup scenarios.",
    name: "Sanket K",
    format: "Kindle Edition",
    date: "25 July 2025",
    verified: false,
  },
  {
    id: 5,
    title: "The Founders Roadmap for Problem-Solution Fit",
    quote:
      "A must-read for all founders. I especially liked the roadmap for the Problem-Solution Fit for any product or service that a founder is working on.",
    name: "Ranjan Varma",
    format: "Hardcover",
    date: "17 July 2025",
    verified: true,
  },
];

const STATS = [
  { value: "35", label: "chapters distilled" },
  { value: "13", label: "AI-powered quests" },
  { value: "5", label: "founder dimensions" },
  { value: "599+", label: "founders in community" },
];

const AMAZON_URL =
  "https://www.amazon.in/Founder-Mode-Escape-Problem-Solution-Million-ebook/dp/B0DWV2BCMV/";

const FAQ_ITEMS = [
  {
    q: "Do I need to have a startup already?",
    a: "No. Whether you're pre-idea, moonlighting, or already scaling — the quests adapt to your stage. Even a vague itch to build something is enough to start Quest 0.",
  },
  {
    q: "Is this really AI, or another gimmicky chatbot?",
    a: "Real GPT-4o. Every response is a genuine Socratic question personalised to your name, founder persona, startup stage, and what you're building. It pushes back on shallow answers and remembers everything you say inside the quest.",
  },
  {
    q: "How is this different from just reading the book?",
    a: "The book gives you the framework. The quest applies it TO YOU. Instead of reading about problem-solution fit, you spend 15 minutes stress-testing YOUR specific killer assumption with an AI that won't let you off the hook. Founders who paid ₹4,997 also get the book PDF included.",
  },
  {
    q: "What's in the 1000 Founders Community?",
    a: "A private WhatsApp group with 599+ founders across India and beyond. Weekly voice chats, real intros, honest feedback, no gurus. You get the invite instantly after payment.",
  },
  {
    q: "What's your refund policy?",
    a: "Quests 0 and 1 are free — that's your full evaluation. After Quest 2 or any paid quest is accessed, no refunds. If you hit a technical bug, email praveen@thefoundermode.in and we'll fix it.",
  },
  {
    q: "Who is Praveen Singh V?",
    a: "Founder of Super Founder Hub, fof.network, and the 1000 Founders Community. Author of The Founder Mode (available on Amazon Kindle). Based in Bangalore. This platform is his 35-chapter playbook turned into an AI mentor.",
  },
];

function hideOnError(e) {
  e.currentTarget.style.display = "none";
}

function ReviewCard({ r }) {
  return (
    <blockquote
      data-testid={`testimonial-${r.id}`}
      className="flex flex-col rounded-xl border border-zinc-800 bg-zinc-950/40 p-6 hover:border-amber-500/30 transition-colors"
    >
      {/* 5-star row */}
      <div className="flex items-center gap-0.5 text-amber-400" aria-label="5 out of 5 stars">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
        ))}
      </div>
      <h3 className="mt-3 text-base font-semibold text-zinc-100 leading-snug break-words">
        {r.title}
      </h3>
      <p className="mt-3 flex-1 text-sm text-zinc-400 italic leading-relaxed break-words">
        &ldquo;{r.quote}&rdquo;
      </p>
      <footer className="mt-5 pt-4 border-t border-zinc-900 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm text-zinc-200 font-medium">{r.name}</span>
        {r.verified && (
          <span
            data-testid={`testimonial-${r.id}-verified`}
            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-emerald-300"
          >
            <Check className="h-2.5 w-2.5" /> verified purchase
          </span>
        )}
        <span className="w-full font-mono text-[10px] text-zinc-600">
          {r.format} · {r.date}
        </span>
      </footer>
    </blockquote>
  );
}

export default function LandingPage() {
  const [, navigate] = useLocation();
  const { user, setUser } = useUser();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Sticky mini-CTA: appears when the primary CTA form scrolls out of view.
  const ctaRef = useRef(null);
  const emailInputRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const node = ctaRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { rootMargin: "-40px 0px 0px 0px", threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  async function handleStart(e) {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await api.login(email.trim(), name.trim());
      setUser(user);
      if (!user.startup_stage) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      if (e instanceof HttpError)
        setError(e.body?.detail?.[0]?.msg || e.body?.detail || "Login failed.");
      else setError("Network error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToCta() {
    ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // slight delay to allow scroll to settle before focus jumps
    setTimeout(() => emailInputRef.current?.focus(), 400);
  }

  // "Buy Founder Pass" click — go straight to /paywall.
  // Anonymous users see inline signup on the paywall itself.
  function handleBuyFounderPass() {
    navigate("/paywall");
  }

  return (
    <main data-testid="landing-page" className="min-h-screen">
      {/* HERO — full-width headline + CTA, no book image here */}
      <section className="relative px-6 sm:px-10 lg:px-20 pt-20 pb-16 lg:pt-28 lg:pb-20">
        <div className="absolute inset-0 -z-10 fmq-aurora" />
        <div className="max-w-4xl mx-auto min-w-0">
          <div
            data-testid="landing-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/[0.06] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300 mb-7"
          >
            <Sparkles className="h-3 w-3" /> the founder mode quest
          </div>
          <h1
            data-testid="landing-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight text-zinc-50 break-words"
          >
            Stop reading about <span className="text-amber-400">Founder Mode</span>.{" "}
            <br className="hidden sm:block" />
            Start <span className="italic font-normal text-amber-200">living</span> it
            <span className="text-amber-400">.</span>
          </h1>
          <p
            data-testid="landing-sub"
            className="mt-7 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed"
          >
            13 AI-powered quests built on 35 chapters of Praveen Singh V&apos;s playbook. An AI
            mentor challenges you, scores you, and builds your personalized roadmap from idea
            to $10M.
          </p>

          <form
            ref={ctaRef}
            onSubmit={handleStart}
            data-testid="landing-cta-form"
            className="mt-10 max-w-md rounded-2xl border border-amber-500/20 bg-zinc-950/60 backdrop-blur-md p-6 shadow-2xl shadow-amber-900/10"
          >
            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
              Name
            </label>
            <Input
              data-testid="landing-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Praveen"
              className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
              disabled={submitting}
              autoComplete="name"
            />
            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2 mt-4">
              Email
            </label>
            <Input
              data-testid="landing-email-input"
              ref={emailInputRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@founder.in"
              className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
              disabled={submitting}
              autoComplete="email"
            />
            {error && (
              <p data-testid="landing-error" className="mt-3 text-xs text-red-400">
                {String(error)}
              </p>
            )}
            <Button
              data-testid="landing-submit-btn"
              type="submit"
              disabled={submitting}
              className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2 group"
            >
              {submitting ? "Starting…" : "Start Free Quest"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <p
              data-testid="landing-trust-marker"
              className="mt-3 text-[12px] text-zinc-400 inline-flex items-center gap-2"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              Join <span className="font-semibold text-amber-300">599+ founders</span> on the quest
            </p>
            <p className="mt-3 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
              <Check className="h-3 w-3 text-emerald-400" />
              Quest 0 & 1 free forever. No credit card needed.
            </p>
            <p data-testid="bookmark-tip" className="mt-2 text-[11px] text-zinc-600 italic">
              Tip: bookmark this page. Use the same email to resume your quest anytime.
            </p>
          </form>
        </div>
      </section>

      {/* BOOK SHOWCASE — physical book photo + caption */}
      <section
        data-testid="book-showcase"
        className="px-6 sm:px-10 lg:px-20 py-20 border-t border-zinc-900 overflow-hidden"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="min-w-0 flex justify-center lg:justify-start">
            <div className="relative">
              <div
                className="absolute -inset-8 rounded-3xl bg-amber-500/15 blur-3xl"
                aria-hidden="true"
              />
              <img
                data-testid="landing-book-showcase"
                src="/founder-mode-hero.jpg"
                alt="The Founder Mode — book by Praveen Singh V, on burgundy velvet"
                loading="lazy"
                onError={hideOnError}
                className="relative w-full max-w-md rounded-xl border border-amber-500/30 shadow-2xl shadow-amber-900/50 rotate-[-3deg] hover:rotate-0 transition-transform duration-500"
              />
            </div>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
              the book
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-zinc-100 leading-tight break-words">
              Based on the 35-chapter playbook by{" "}
              <span className="text-amber-300">Praveen Singh V</span>
            </h2>
            <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-xl">
              <em className="text-zinc-200 not-italic font-medium">The Founder Mode</em> is the
              book that started it all — a step-by-step playbook for escaping 9-5, finding
              problem-solution fit, and scaling to $10 million. This quest platform brings each
              chapter to life through AI-powered Socratic mentorship.
            </p>
            <a
              data-testid="book-showcase-amazon-link"
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200 border-b border-amber-500/30 hover:border-amber-400 pb-0.5 transition-colors"
            >
              Read on Amazon
              <span
                className="font-mono text-base leading-none"
                style={{ color: "#ff9900" }}
                aria-hidden="true"
              >
                →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 real product screenshots */}
      <section
        data-testid="how-it-works"
        className="w-full py-20 px-6 sm:px-10 lg:px-20 border-t border-zinc-900"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-400 mb-3">
            how it works
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-100 mb-3 tracking-tight break-words">
            What actually happens when you sign up.
          </h2>
          <p className="text-zinc-400 mb-12 max-w-2xl leading-relaxed">
            No lectures. No videos to watch. Just an AI mentor asking you the right
            questions — and remembering everything you say.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 — persona reveal */}
            <div
              data-testid="hiw-card-1"
              className="rounded-2xl border border-amber-500/20 bg-zinc-900/50 p-5"
            >
              <div className="text-amber-400 font-mono text-sm mb-3">01</div>
              <img
                src="/how-it-works-1-persona.png"
                alt="Founder persona reveal after Quest 0"
                loading="lazy"
                onError={hideOnError}
                className="rounded-lg border border-zinc-800 mb-4 w-full aspect-[4/3] object-cover object-top"
              />
              <h3 className="font-bold text-lg text-zinc-100 mb-2">
                Discover your founder mode
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Answer 3 rapid-fire questions. The AI reveals which of 6 founder personas
                you are — the same archetypes from Praveen&apos;s book.
              </p>
            </div>

            {/* Card 2 — mid-conversation mentorship */}
            <div
              data-testid="hiw-card-2"
              className="rounded-2xl border border-amber-500/20 bg-zinc-900/50 p-5"
            >
              <div className="text-amber-400 font-mono text-sm mb-3">02</div>
              <img
                src="/how-it-works-2-mentor.png"
                alt="Mid-conversation Socratic AI mentorship in Quest 1"
                loading="lazy"
                onError={hideOnError}
                className="rounded-lg border border-zinc-800 mb-4 w-full aspect-[4/3] object-cover object-top"
              />
              <h3 className="font-bold text-lg text-zinc-100 mb-2">
                Get 1-on-1 AI mentorship
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Real Socratic conversations. The AI pushes back on shallow answers,
                personalises based on your startup stage, and probes for the specific
                decision you&apos;re avoiding.
              </p>
            </div>

            {/* Card 3 — scorecard with radar */}
            <div
              data-testid="hiw-card-3"
              className="rounded-2xl border border-amber-500/20 bg-zinc-900/50 p-5"
            >
              <div className="text-amber-400 font-mono text-sm mb-3">03</div>
              <img
                src="/how-it-works-3-scorecard.png"
                alt="Founder scorecard with radar chart and five dimension bars"
                loading="lazy"
                onError={hideOnError}
                className="rounded-lg border border-zinc-800 mb-4 w-full aspect-[4/3] object-cover object-top"
              />
              <h3 className="font-bold text-lg text-zinc-100 mb-2">
                Get scored, get your roadmap
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Every quest scores you across 5 founder dimensions: clarity,
                problem-solution fit, empathy, growth, execution. You see exactly where
                to focus next.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-6 sm:px-10 lg:px-20 py-14 border-t border-zinc-900">
        <div
          data-testid="landing-stats"
          className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-mono text-2xl text-amber-400 leading-none">{s.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* QUEST MAP PREVIEW */}
      <section className="px-6 sm:px-10 lg:px-20 py-20 border-b border-zinc-900">
        <div
          data-testid="landing-map-preview"
          className="max-w-3xl mx-auto rounded-2xl border border-amber-500/15 bg-zinc-950/40 backdrop-blur-sm p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-300">
                the path
              </p>
              <h2 className="mt-1 text-lg font-semibold text-zinc-100">13 quests, 4 parts.</h2>
            </div>
            <span className="font-mono text-xs text-zinc-500">0 / 13</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {QUEST_META.map((q) => {
              const Icon = ICON_MAP[q.icon];
              return (
                <div
                  key={q.id}
                  className="flex items-center gap-3 rounded-lg border border-zinc-800/80 bg-zinc-950/60 px-3 py-2.5 min-w-0"
                >
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md shrink-0 ${
                      q.free
                        ? "border border-amber-500/30 bg-amber-500/10 text-amber-300"
                        : "bg-zinc-900 text-zinc-700"
                    }`}
                  >
                    {q.free ? <Icon className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-zinc-600">
                        Q{String(q.id).padStart(2, "0")}
                      </span>
                      <span className="truncate text-sm text-zinc-300">{q.title}</span>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-600 shrink-0">+{q.xp}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — real Amazon reviews of the book */}
      <section
        data-testid="testimonials-section"
        className="px-6 sm:px-10 lg:px-20 py-20 border-b border-zinc-900"
      >
        <div className="max-w-6xl mx-auto">
          <div data-testid="testimonials-header">
            <p className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300 mb-3">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              amazon reviews · 4.9 ★ · 39 global ratings
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-zinc-100 tracking-tight max-w-2xl">
              What readers say about the book
            </h2>
            <p className="mt-3 text-sm text-zinc-500 max-w-2xl leading-relaxed">
              The quest platform brings this book to life. Below are real Amazon reviews of
              Praveen&apos;s book — the source material for the 13 AI-powered quests.
            </p>
          </div>

          {/* Top row — 3 reviews */}
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {AMAZON_REVIEWS.slice(0, 3).map((r) => (
              <ReviewCard key={r.id} r={r} />
            ))}
          </div>
          {/* Bottom row — 2 reviews, centered on desktop */}
          <div className="mt-6 grid md:grid-cols-2 gap-6 md:max-w-4xl md:mx-auto">
            {AMAZON_REVIEWS.slice(3, 5).map((r) => (
              <ReviewCard key={r.id} r={r} />
            ))}
          </div>

          {/* Read all reviews link */}
          <div className="mt-10 text-center">
            <a
              data-testid="testimonials-amazon-link"
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200 border-b border-amber-500/30 hover:border-amber-400 pb-0.5 transition-colors"
            >
              Read all 39 reviews on Amazon
              <span
                className="font-mono text-base leading-none"
                style={{ color: "#ff9900" }}
                aria-hidden="true"
              >
                →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* PRICING — Free vs Founder Pass */}
      <section className="px-6 sm:px-10 lg:px-20 py-20">
        <div data-testid="pricing-block" className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300 mb-2">
              pricing
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-zinc-100 tracking-tight">
              Start free. Upgrade when you&apos;re hooked.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {/* FREE CARD */}
            <div
              data-testid="pricing-free-card"
              className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-6 sm:p-7 flex flex-col"
            >
              <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                free
              </p>
              <h3 className="mt-2 text-2xl font-semibold text-zinc-100">
                Get a taste
              </h3>
              <div className="mt-4 font-mono text-4xl text-zinc-200">
                ₹0
                <span className="ml-2 align-middle text-xs text-zinc-500 font-sans">
                  forever
                </span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-400 flex-1">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Quest 0: Discover your founder mode</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Quest 1: Escape your 9-5</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Personalized Socratic mentorship</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>No credit card needed</span>
                </li>
              </ul>
              <Button
                data-testid="pricing-free-cta"
                onClick={scrollToCta}
                variant="outline"
                className="mt-7 h-11 border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-800 hover:text-zinc-50 gap-2"
              >
                Start Free Quest <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            {/* FOUNDER PASS CARD — highlighted */}
            <div
              data-testid="pricing-paid-card"
              className="relative rounded-2xl border-2 border-amber-500/50 bg-amber-500/[0.05] p-6 sm:p-7 flex flex-col shadow-[0_0_60px_-15px_rgba(245,158,11,0.35)]"
            >
              <div className="absolute -top-3 right-6 rounded-full bg-amber-500 text-zinc-950 font-mono text-[10px] uppercase tracking-widest px-3 py-1">
                most popular
              </div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-amber-300">
                founder pass
              </p>
              <h3 className="mt-2 text-2xl font-semibold text-zinc-50">
                Go full Founder Mode
              </h3>
              <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                <span className="font-mono text-4xl text-amber-300">₹4,997</span>
                <span className="font-mono text-xl text-zinc-500">/ $197</span>
              </div>
              <p className="mt-1 text-xs text-zinc-500">international via Stripe</p>
              <ul className="mt-6 space-y-3 text-sm text-zinc-300 flex-1">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <span>All 13 AI-powered quests</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <span>
                    <em className="text-zinc-100 not-italic font-medium">The Founder Mode</em>{" "}
                    book (PDF, 35 chapters) —{" "}
                    <span className="line-through text-zinc-600">₹999</span>{" "}
                    <span className="text-amber-300">included</span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <span>
                    Lifetime access to <strong className="text-zinc-100">1000 Founders
                    Community</strong> — 599+ founders on WhatsApp
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <span>Monthly leaderboard + Gold / Silver / Bronze awards</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <span>Lifetime access, one-time payment</span>
                </li>
              </ul>
              <Button
                data-testid="pricing-paid-cta"
                onClick={handleBuyFounderPass}
                className="mt-7 h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
              >
                Buy Founder Pass <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
            Cannot decide? Start free — you&apos;ll get a personalised nudge to upgrade
            after Quest 1.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="faq"
        className="w-full py-16 px-6 sm:px-10 lg:px-20 border-t border-zinc-900"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-400 mb-3">
            questions
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-100 mb-8 tracking-tight">
            You probably want to know...
          </h2>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i + 1}`}
                className="group rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40 transition-colors open:border-amber-500/40 open:bg-zinc-900/60"
              >
                <summary className="cursor-pointer p-4 font-medium text-zinc-100 marker:text-amber-400 list-outside pl-6 select-none">
                  {item.q}
                </summary>
                <div className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* STICKY MINI-CTA — only when the main hero CTA is off-screen */}
      {showSticky && (
        <button
          data-testid="sticky-mini-cta"
          onClick={scrollToCta}
          className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-40 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold h-11 px-5 shadow-2xl shadow-amber-500/30 border border-amber-300/40 transition-all"
          aria-label="Scroll back up to the Start Free form"
        >
          <span>Start Free</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </main>
  );
}
